import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useApp } from '../../context/AppContext';
import { PDFDocument } from 'pdf-lib';
import { downloadBlob } from '../../utils/fileUtils';
import './PDFConverter.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const SUPPORTED_FORMATS = {
  images: {
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'] },
    label: 'Images',
    description: 'JPG, PNG, GIF, WebP',
    icon: '🖼️',
    clientSide: true
  },
  html: {
    accept: { 'text/html': ['.html', '.htm'] },
    label: 'HTML',
    description: 'HTML files (basic)',
    icon: '🌐',
    clientSide: true
  },
  word: {
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    },
    label: 'Word',
    description: 'DOC, DOCX',
    icon: '📝',
    clientSide: false,
    endpoint: '/api/convert/word/'
  },
  excel: {
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    label: 'Excel',
    description: 'XLS, XLSX',
    icon: '📊',
    clientSide: false,
    endpoint: '/api/convert/excel/'
  },
  powerpoint: {
    accept: {
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'application/vnd.ms-powerpoint': ['.ppt']
    },
    label: 'PowerPoint',
    description: 'PPT, PPTX',
    icon: '📽️',
    clientSide: false,
    endpoint: '/api/convert/powerpoint/'
  }
};

const PDFConverter = () => {
  const { showNotification, setCurrentView } = useApp();
  const [selectedFormat, setSelectedFormat] = useState('images');
  const [files, setFiles] = useState([]);
  const [isConverting, setIsConverting] = useState(false);
  const [outputFilename, setOutputFilename] = useState('converted');
  const [imageLayout, setImageLayout] = useState('one-per-page'); // one-per-page, fit-to-page

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) {
      showNotification('Please select valid files', 'warning');
      return;
    }

    const newFiles = acceptedFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));

    setFiles(prev => {
      const updated = [...prev, ...newFiles];
      // Set default filename from first file (without extension)
      if (prev.length === 0 && newFiles.length > 0) {
        const firstFileName = newFiles[0].name;
        const nameWithoutExt = firstFileName.replace(/\.[^/.]+$/, '');
        setOutputFilename(nameWithoutExt);
      }
      return updated;
    });
  }, [showNotification]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: SUPPORTED_FORMATS[selectedFormat].accept,
    multiple: true
  });

  const handleRemoveFile = (id) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  const handleClearAll = () => {
    files.forEach(f => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setFiles([]);
    setOutputFilename('converted');
  };

  const convertImagesToPDF = async () => {
    const pdfDoc = await PDFDocument.create();
    const failedFiles = [];

    for (const fileData of files) {
      try {
        // Use canvas approach for all image types for better compatibility
        const img = new Image();

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = fileData.preview;
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        // Convert to JPEG for better compatibility with pdf-lib
        const jpegData = await new Promise((resolve, reject) => {
          canvas.toBlob(blob => {
            if (!blob) {
              reject(new Error('Failed to convert image'));
              return;
            }
            blob.arrayBuffer().then(buffer => resolve(new Uint8Array(buffer)));
          }, 'image/jpeg', 0.92);
        });

        const image = await pdfDoc.embedJpg(jpegData);
        const { width, height } = image;

        if (imageLayout === 'one-per-page') {
          // Add page sized to image
          const page = pdfDoc.addPage([width, height]);
          page.drawImage(image, {
            x: 0,
            y: 0,
            width,
            height
          });
        } else {
          // Fit to standard letter size (612 x 792 points)
          const pageWidth = 612;
          const pageHeight = 792;
          const page = pdfDoc.addPage([pageWidth, pageHeight]);

          const scale = Math.min(
            (pageWidth - 40) / width,
            (pageHeight - 40) / height
          );

          const scaledWidth = width * scale;
          const scaledHeight = height * scale;

          page.drawImage(image, {
            x: (pageWidth - scaledWidth) / 2,
            y: (pageHeight - scaledHeight) / 2,
            width: scaledWidth,
            height: scaledHeight
          });
        }
      } catch (error) {
        console.error(`Failed to convert ${fileData.name}:`, error);
        failedFiles.push(fileData.name);
      }
    }

    if (failedFiles.length > 0) {
      showNotification(`Format not supported: ${failedFiles.join(', ')}`, 'warning');
    }

    if (pdfDoc.getPageCount() === 0) {
      throw new Error('No images could be converted');
    }

    return pdfDoc;
  };

  const convertHTMLToPDF = async () => {
    // Basic HTML to PDF conversion using print-to-PDF approach
    const pdfDoc = await PDFDocument.create();

    for (const fileData of files) {
      const text = await fileData.file.text();

      // Create a simple page with the HTML content as text
      // Note: Full HTML rendering requires a browser print or external service
      const page = pdfDoc.addPage([612, 792]);
      const { height } = page.getSize();

      // Strip HTML tags for basic text extraction
      const plainText = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

      page.drawText(plainText.substring(0, 2000), {
        x: 50,
        y: height - 50,
        size: 10,
        maxWidth: 512
      });
    }

    showNotification('Note: HTML conversion is basic. For full rendering, use browser print.', 'info');
    return pdfDoc;
  };

  const convertViaAPI = async (file, endpoint) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Conversion failed' }));
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }

    return response.blob();
  };

  const convertServerSideFiles = async () => {
    const format = SUPPORTED_FORMATS[selectedFormat];
    const convertedBlobs = [];
    const failedFiles = [];

    for (const fileData of files) {
      try {
        const blob = await convertViaAPI(fileData.file, format.endpoint);
        convertedBlobs.push({
          blob,
          name: fileData.name.replace(/\.[^/.]+$/, '.pdf')
        });
      } catch (error) {
        console.error(`Failed to convert ${fileData.name}:`, error);
        failedFiles.push({ name: fileData.name, error: error.message });
      }
    }

    if (failedFiles.length > 0) {
      const errorMsg = failedFiles.map(f => `${f.name}: ${f.error}`).join('; ');
      showNotification(`Some files failed: ${errorMsg}`, 'warning');
    }

    if (convertedBlobs.length === 0) {
      throw new Error('No files could be converted');
    }

    return convertedBlobs;
  };

  const handleConvert = async () => {
    if (files.length === 0) {
      showNotification('Please add files to convert', 'warning');
      return;
    }

    setIsConverting(true);

    try {
      const format = SUPPORTED_FORMATS[selectedFormat];

      if (format.clientSide) {
        // Client-side conversion for images and HTML
        let pdfDoc;

        if (selectedFormat === 'images') {
          pdfDoc = await convertImagesToPDF();
        } else if (selectedFormat === 'html') {
          pdfDoc = await convertHTMLToPDF();
        }

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        downloadBlob(blob, `${outputFilename || 'converted'}.pdf`);
      } else {
        // Server-side conversion for Office documents
        const convertedBlobs = await convertServerSideFiles();

        if (convertedBlobs.length === 1) {
          // Single file - download directly
          downloadBlob(convertedBlobs[0].blob, `${outputFilename || 'converted'}.pdf`);
        } else {
          // Multiple files - download each with numbered suffix
          convertedBlobs.forEach((item, index) => {
            const filename = `${outputFilename || 'converted'}_${index + 1}.pdf`;
            downloadBlob(item.blob, filename);
          });
        }
      }

      showNotification('Files converted to PDF successfully!', 'success');
    } catch (error) {
      console.error('Error converting to PDF:', error);
      showNotification(error.message || 'Failed to convert files', 'error');
    }

    setIsConverting(false);
  };

  return (
    <div className="converter-container">
      <div className="converter-header">
        <button className="btn btn-ghost" onClick={() => setCurrentView('upload')}>
          ← Back
        </button>
        <h2>Convert to PDF</h2>
      </div>

      <div className="converter-content">
        <div className="format-selector">
          <h3>Select Format</h3>
          <div className="format-options">
            {Object.entries(SUPPORTED_FORMATS).map(([key, format]) => (
              <button
                key={key}
                className={`format-option ${selectedFormat === key ? 'active' : ''}`}
                onClick={() => {
                  setSelectedFormat(key);
                  handleClearAll();
                }}
              >
                <span className="format-icon">{format.icon}</span>
                <span className="format-label">{format.label}</span>
                <span className="format-desc">{format.description}</span>
              </button>
            ))}
          </div>
          {!SUPPORTED_FORMATS[selectedFormat].clientSide && (
            <p className="format-note">
              Note: {SUPPORTED_FORMATS[selectedFormat].label} conversion is processed by the server.
              Files are sent securely and deleted after conversion.
            </p>
          )}
        </div>

        <div
          {...getRootProps()}
          className={`converter-dropzone ${isDragActive ? 'active' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="dropzone-content">
            <span className="dropzone-icon">{SUPPORTED_FORMATS[selectedFormat].icon}</span>
            <p>
              {isDragActive
                ? `Drop ${SUPPORTED_FORMATS[selectedFormat].label} files here`
                : `Drag & drop ${SUPPORTED_FORMATS[selectedFormat].description} files here`}
            </p>
            <span>or click to select files</span>
          </div>
        </div>

        {selectedFormat === 'images' && files.length > 0 && (
          <div className="layout-selector">
            <h3>Image Layout</h3>
            <div className="layout-options">
              <label className={`layout-option ${imageLayout === 'one-per-page' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="layout"
                  value="one-per-page"
                  checked={imageLayout === 'one-per-page'}
                  onChange={(e) => setImageLayout(e.target.value)}
                />
                <span className="layout-label">Original Size</span>
                <span className="layout-desc">Each image on its own page at original size</span>
              </label>
              <label className={`layout-option ${imageLayout === 'fit-to-page' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="layout"
                  value="fit-to-page"
                  checked={imageLayout === 'fit-to-page'}
                  onChange={(e) => setImageLayout(e.target.value)}
                />
                <span className="layout-label">Fit to Page</span>
                <span className="layout-desc">Scale images to fit standard letter size</span>
              </label>
            </div>
          </div>
        )}

        {files.length > 0 && (
          <div className="files-section">
            <div className="files-header">
              <h3>Files to Convert ({files.length})</h3>
              <button className="btn btn-ghost btn-sm" onClick={handleClearAll}>
                Clear All
              </button>
            </div>
            <div className="convert-files-list">
              {files.map((file) => (
                <div key={file.id} className="convert-file-item">
                  {file.preview ? (
                    <img src={file.preview} alt={file.name} className="file-preview" />
                  ) : (
                    <div className="file-icon-placeholder">📄</div>
                  )}
                  <div className="file-details">
                    <div className="file-name">{file.name}</div>
                    <div className="file-type">{file.type}</div>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm remove-btn"
                    onClick={() => handleRemoveFile(file.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {files.length > 0 && (
          <div className="output-section">
            <label>
              <span>Output filename:</span>
              <div className="filename-input">
                <input
                  type="text"
                  value={outputFilename}
                  onChange={(e) => setOutputFilename(e.target.value)}
                  placeholder="converted"
                />
                <span className="extension">.pdf</span>
              </div>
            </label>
          </div>
        )}
      </div>

      <div className="converter-footer">
        <button
          className="btn btn-success"
          onClick={handleConvert}
          disabled={isConverting || files.length === 0}
        >
          {isConverting ? (
            <>
              <span className="spinner"></span>
              Converting...
            </>
          ) : (
            `Convert ${files.length} File${files.length !== 1 ? 's' : ''} to PDF`
          )}
        </button>
      </div>
    </div>
  );
};

export default PDFConverter;
